/**
 * The AI setting for each value (migration 920, value_ai_modes) and what lies
 * behind an AI value: the setting itself, and the agent's own record of how it
 * reached the value (agent_decision_records).
 *
 * The setting is written only through set_value_ai_mode (admins; "provides"
 * also needs the setter's permission to accept values, because a standing rule
 * accepts in their name).
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import { getSupabaseClient } from './supabaseClient';
import type { ValueAiMode, ValueAiSetting, ValueReplacement } from './valueStatus';

function toSetting(r: {
  workspace_id: string;
  attribute_key: string;
  mode: string;
  agent_type: string | null;
  accept_floor: number;
  set_by: string | null;
  set_at: string;
  reason: string | null;
}): ValueAiSetting {
  return {
    workspaceId: r.workspace_id,
    attributeKey: r.attribute_key,
    mode: (['off', 'recommend', 'provide'].includes(r.mode) ? r.mode : 'off') as ValueAiMode,
    agentType: r.agent_type,
    acceptFloor: Number(r.accept_floor),
    setBy: r.set_by,
    setAt: r.set_at,
    reason: r.reason,
  };
}

export async function fetchValueAiSettings(workspaceId: string): Promise<ValueAiSetting[]> {
  const { data, error } = await getTypedSupabaseClient()
    .from('value_ai_modes')
    .select('*')
    .eq('workspace_id', workspaceId);
  if (error) throw new Error(error.message);
  return (data ?? []).map(toSetting);
}

export async function setValueAiSetting(input: {
  workspaceId: string;
  attributeKey: string;
  mode: ValueAiMode;
  agentType: string | null;
  acceptFloor: number;
  reason: string | null;
}): Promise<{ setting: ValueAiSetting | null; error: string | null }> {
  const { data, error } = await getTypedSupabaseClient().rpc('set_value_ai_mode', {
    p_workspace_id: input.workspaceId,
    p_attribute_key: input.attributeKey,
    p_mode: input.mode,
    // The database takes NULL for both; generated RPC argument types cannot say so.
    p_agent_type: (input.mode === 'off' ? null : input.agentType) ?? (null as unknown as string),
    p_accept_floor: input.acceptFloor,
    p_reason: input.reason ?? (null as unknown as string),
  });
  if (error) return { setting: null, error: error.message };
  return { setting: data ? toSetting(data) : null, error: null };
}

/** One step of an agent's reasoning about a value, as it recorded it. */
export interface ValueDecisionRecord {
  id: string;
  agentType: string;
  outputKind: string;
  proposalId: string | null;
  proposedValue: unknown;
  confidence: number | null;
  rationale: string;
  evidence: Array<{ kind?: string; id?: string; label?: string; used?: boolean }>;
  model: string | null;
  stackVersion: string | null;
  reviewOutcome: string | null;
  reviewedBy: string | null;
  /** The administrator an automatic run worked for (the agent's sponsor when it ran). */
  sponsorUserId: string | null;
  /** The person who ran it by hand; null on an automatic run. */
  runByUserId: string | null;
  createdAt: string;
}

/**
 * The agent's most recent records for one value of one item, newest first.
 * With a null attribute key, every value of the item.
 */
export async function fetchValueDecisionRecords(
  workspaceId: string,
  itemId: string,
  attributeKey: string | null,
  limit = 3,
): Promise<ValueDecisionRecord[]> {
  // agent_decision_records is not in the generated types yet; read it the way
  // TemplateAgentSettings and ValueEstimatorReportPage do.
  let query = getSupabaseClient()
    .from('agent_decision_records')
    .select('id, agent_type, output_kind, output_id, proposed_value, confidence, rationale, evidence, model, stack_version, review_outcome, reviewed_by, sponsor_user_id, run_by_user_id, created_at')
    .eq('workspace_id', workspaceId)
    .eq('object_id', itemId);
  if (attributeKey != null) query = query.eq('field', attributeKey);
  const { data, error } = await query.order('created_at', { ascending: false }).limit(limit);
  if (error) throw new Error(error.message);
  return ((data ?? []) as Array<Record<string, unknown> & {
    id: string; agent_type: string; output_kind: string; output_id: string | null; proposed_value: unknown;
    confidence: number | null; rationale: string; evidence: unknown; model: string | null; stack_version: string | null;
    review_outcome: string | null; reviewed_by: string | null; sponsor_user_id: string | null; run_by_user_id: string | null;
    created_at: string;
  }>).map((r) => ({
    id: r.id,
    agentType: r.agent_type,
    outputKind: r.output_kind,
    proposalId: r.output_id,
    proposedValue: r.proposed_value,
    confidence: r.confidence,
    rationale: r.rationale,
    evidence: Array.isArray(r.evidence) ? (r.evidence as ValueDecisionRecord['evidence']) : [],
    model: r.model,
    stackVersion: r.stack_version,
    reviewOutcome: r.review_outcome,
    reviewedBy: r.reviewed_by,
    sponsorUserId: r.sponsor_user_id ?? null,
    runByUserId: r.run_by_user_id ?? null,
    createdAt: r.created_at,
  }));
}

/**
 * Who an agent's proposal was made for, from that proposal's own decision record
 * (never the agent's settings today, which may have changed since): "run by <person>"
 * when a person ran it by hand, "for <sponsor>" when it ran by itself. Null when
 * the record names no one the caller can name.
 */
export function agentRunPersonWords(
  record: Pick<ValueDecisionRecord, 'sponsorUserId' | 'runByUserId'> | null | undefined,
  nameOf: (id: string | null | undefined) => string | null,
): string | null {
  if (!record) return null;
  if (record.runByUserId) {
    const runBy = nameOf(record.runByUserId);
    return runBy ? `run by ${runBy}` : null;
  }
  const sponsor = record.sponsorUserId ? nameOf(record.sponsorUserId) : null;
  return sponsor ? `for ${sponsor}` : null;
}

/**
 * The values this agent works on: every value whose AI setting is "recommends"
 * or "provides" with this agent. The one place that decides it (migration 920);
 * an agent's own behavior_config.attributes no longer does.
 */
export async function fetchValuesForAgent(workspaceId: string, agentType: string): Promise<string[]> {
  const { data, error } = await getTypedSupabaseClient()
    .from('value_ai_modes')
    .select('attribute_key')
    .eq('workspace_id', workspaceId)
    .eq('agent_type', agentType)
    .in('mode', ['recommend', 'provide']);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => r.attribute_key);
}

/**
 * Every change that took an official value away (accepted_value_revisions,
 * migration 821: change 'delete' or 'update', no session scope). valueStatus()
 * reads the latest one for a value to tell "a person replaced an agent's value"
 * (ai_overridden) from "a person set it" (human).
 */
export async function fetchValueReplacements(workspaceId: string): Promise<ValueReplacement[]> {
  const { data, error } = await getTypedSupabaseClient()
    .from('accepted_value_revisions')
    .select('item_id, attribute_key, scale_id, old_value, old_from_proposal_id, changed_by, changed_at')
    .eq('workspace_id', workspaceId)
    .is('session_scope', null)
    .in('change', ['delete', 'update'])
    .order('changed_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => ({
    itemId: r.item_id,
    attributeKey: r.attribute_key,
    scaleId: r.scale_id,
    oldValue: r.old_value,
    oldFromProposalId: r.old_from_proposal_id,
    changedBy: r.changed_by,
    changedAt: r.changed_at,
  }));
}
